import { appendRecord, isPocketBaseMode, loadSettings, Repository, saveSettings, updateRecord, verifyLiveBusinessSettings } from '../data/repository';
import type { Client, Invoice, InvoiceItem, RecurringProfile } from '../types';
import { dispatchEmailJob } from '../config';
import { getProfessionalInvoiceEmailDraft, renderEmailTemplate } from './emailTemplates';
import { calculateInvoiceEditorFinancials } from './invoiceFinancials';
import { recalculateInvoiceItem, roundMoney } from './calculations';
import { getApplicableRecurringPriceChange, type RecurringPriceChange } from './recurringPricingSchedule';
import { getNextInvoiceNumber } from './invoiceNumbering';
import { generateInvoicePdf } from './pdf';
import { isValidEmail, validateAndDedupeRecipients } from './validators';

export type RecurringSchedule = 'weekly' | 'fortnightly' | 'every_28_days' | 'monthly' | 'quarterly' | 'annual';
export type RecurringDueDateRule = 'immediate' | 'net_7' | 'net_14' | 'net_30';

export interface RecurringInvoiceTemplate {
  items: InvoiceItem[];
  notes?: string;
  internalNotes?: string;
  termsSnapshot?: string;
  currency?: 'AUD' | 'USD';
  sourceInvoiceId?: string;
  schedule?: RecurringSchedule;
  dueDateRule?: RecurringDueDateRule;
  pricingMode?: 'template' | 'multiplier' | 'custom_total';
  pricingMultiplier?: number;
  pricingTargetTotalInclTax?: number;
  pricingTargetSubtotalExTax?: number;
  pricingTargetBasis?: 'inclusive' | 'exclusive';
  pricingSourceSchedule?: RecurringSchedule;
  scheduledPriceChanges?: RecurringPriceChange[];
}

export interface RecurringGenerationResult {
  status: 'generated' | 'reconciled' | 'skipped';
  invoice?: Invoice;
  message: string;
  emailQueued?: boolean;
}

const PROCESSOR_INTERVAL_LOCK_MS = 2 * 60 * 1000;
let activeProcessor: Promise<RecurringProcessingSummary> | null = null;
let lastProcessorStartedAt = 0;

const parseDateOnly = (value: string): Date => {
  const match = String(value || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) throw new Error(`Invalid recurring date: ${value}`);
    return new Date(Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate()));
  }
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
};

const formatDateOnly = (date: Date): string => date.toISOString().slice(0, 10);

const getLocalToday = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const addDays = (value: string, days: number): string => {
  const date = parseDateOnly(value);
  date.setUTCDate(date.getUTCDate() + days);
  return formatDateOnly(date);
};

const addMonthsClamped = (value: string, months: number): string => {
  const source = parseDateOnly(value);
  const day = source.getUTCDate();
  const sourceLastDay = new Date(Date.UTC(source.getUTCFullYear(), source.getUTCMonth() + 1, 0)).getUTCDate();
  const targetFirst = new Date(Date.UTC(source.getUTCFullYear(), source.getUTCMonth() + months, 1));
  const targetLastDay = new Date(Date.UTC(targetFirst.getUTCFullYear(), targetFirst.getUTCMonth() + 1, 0)).getUTCDate();
  targetFirst.setUTCDate(day === sourceLastDay ? targetLastDay : Math.min(day, targetLastDay));
  return formatDateOnly(targetFirst);
};

const addYearsClamped = (value: string, years: number): string => {
  const source = parseDateOnly(value);
  const month = source.getUTCMonth();
  const day = source.getUTCDate();
  const targetFirst = new Date(Date.UTC(source.getUTCFullYear() + years, month, 1));
  const lastDay = new Date(Date.UTC(targetFirst.getUTCFullYear(), month + 1, 0)).getUTCDate();
  targetFirst.setUTCDate(Math.min(day, lastDay));
  return formatDateOnly(targetFirst);
};

export const getRecurringSchedule = (profile: Partial<RecurringProfile> | any): RecurringSchedule => {
  const configured = profile?.baseInvoiceTemplate?.schedule;
  if (['weekly', 'fortnightly', 'every_28_days', 'monthly', 'quarterly', 'annual'].includes(configured)) {
    return configured as RecurringSchedule;
  }
  if (profile?.frequency === 'weekly' || profile?.frequency === 'annual') return profile.frequency;
  return 'monthly';
};

export const getStoredRecurringFrequency = (schedule: RecurringSchedule): RecurringProfile['frequency'] => {
  if (schedule === 'annual') return 'annual';
  if (schedule === 'weekly' || schedule === 'fortnightly' || schedule === 'every_28_days') return 'weekly';
  return 'monthly';
};

export const getRecurringDueDateRule = (profile: Partial<RecurringProfile> | any): RecurringDueDateRule => {
  const configured=profile?.baseInvoiceTemplate?.dueDateRule;
  if (['immediate','net_7','net_14','net_30'].includes(configured)) return configured as RecurringDueDateRule;
  const stored=profile?.dueDateRule;
  if (stored==='immediate' || stored==='net_30') return stored;
  return 'net_14';
};

export const getStoredRecurringDueDateRule = (rule: RecurringDueDateRule): RecurringProfile['dueDateRule'] => {
  if (rule==='immediate') return 'immediate';
  if (rule==='net_30') return 'net_30';
  return 'net_14';
};

export const getRecurringScheduleLabel = (scheduleOrProfile: RecurringSchedule | Partial<RecurringProfile> | any): string => {
  const schedule: RecurringSchedule = typeof scheduleOrProfile === 'string' ? scheduleOrProfile as RecurringSchedule : getRecurringSchedule(scheduleOrProfile);
  const labels: Record<RecurringSchedule, string> = {
    weekly: 'Weekly',
    fortnightly: 'Fortnightly',
    every_28_days: 'Every 28 Days',
    monthly: 'Monthly',
    quarterly: 'Quarterly',
    annual: 'Annual',
  };
  return labels[schedule];
};

export const advanceRecurringDate = (value: string, schedule: RecurringSchedule): string => {
  switch (schedule) {
    case 'weekly': return addDays(value, 7);
    case 'fortnightly': return addDays(value, 14);
    case 'every_28_days': return addDays(value, 28);
    case 'monthly': return addMonthsClamped(value, 1);
    case 'quarterly': return addMonthsClamped(value, 3);
    case 'annual': return addYearsClamped(value, 1);
  }
};

export const calculateRecurringServicePeriod = (startDate: string, schedule: RecurringSchedule): { start: string; end: string } => {
  const start = String(startDate || '').slice(0, 10);
  if (!start) return { start: '', end: '' };
  const nextStart = advanceRecurringDate(start, schedule);
  return { start, end: addDays(nextStart, -1) };
};

export const calculateRecurringInvoiceDate = (servicePeriodStart: string, billingInAdvanceDays: number): string => {
  const start = String(servicePeriodStart || '').slice(0, 10);
  if (!start) return '';
  return addDays(start, -Math.max(0, Number(billingInAdvanceDays || 0)));
};

export const getRecurringPricingTargetTotal = (template: Partial<RecurringInvoiceTemplate> | null | undefined): number => {
  if (template?.pricingMode !== 'custom_total') return 0;
  const target = Number(template.pricingTargetTotalInclTax || 0);
  return Number.isFinite(target) && target > 0 ? roundMoney(target) : 0;
};


export const getRecurringPricingMultiplier = (template: Partial<RecurringInvoiceTemplate> | null | undefined): number => {
  if (template?.pricingMode !== 'multiplier') return 1;
  const multiplier = Number(template.pricingMultiplier || 1);
  return Number.isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
};

export const buildRecurringTemplateFromInvoice = (invoice: Invoice, schedule: RecurringSchedule): RecurringInvoiceTemplate => ({
  items: (invoice.items || []).map(item => ({ ...item })),
  notes: invoice.notes || '',
  internalNotes: invoice.internalNotes || '',
  termsSnapshot: invoice.termsSnapshot || '',
  currency: invoice.currency,
  sourceInvoiceId: invoice.id,
  schedule,
});

const cycleKeyForProfile = (profile: RecurringProfile | any): string => {
  const schedule = getRecurringSchedule(profile);
  const start = String(profile.servicePeriodStart || profile.startDate || profile.nextInvoiceDate || '').slice(0, 10);
  const calculated = calculateRecurringServicePeriod(start, schedule);
  const end = String(profile.servicePeriodEnd || calculated.end || '').slice(0, 10);
  const invoiceDate = String(profile.nextInvoiceDate || start).slice(0, 10);
  return `${profile.id}_${invoiceDate}_${start}_${end}`;
};

const markerForCycle = (cycleKey: string): string => `[InvoiceFlow recurring cycle: ${cycleKey}]`;

const appendInternalMarker = (notes: string | undefined, marker: string): string => {
  const clean = String(notes || '').trim();
  return clean ? `${clean}\n${marker}` : marker;
};

export const applyRecurringPricingMultiplier = (item: InvoiceItem, pricingMultiplier = 1): InvoiceItem => {
  const multiplier = Number.isFinite(pricingMultiplier) && pricingMultiplier > 0 ? pricingMultiplier : 1;
  return {
    ...item,
    // Scale monetary line pricing rather than quantity. This preserves the
    // template's quantity semantics while proportionally scaling any fixed
    // line discount as well.
    unitPrice: Number(item.unitPrice || 0) * multiplier,
    discount: Number(item.discount || 0) * multiplier,
  };
};

interface RecurringPricedItemsResult {
  items: InvoiceItem[];
  subtotal: number;
  taxAmount: number;
  total: number;
  roundingAdjustment: number;
  effectiveMultiplier: number;
}

const recalculatePricedItems = (
  sourceItems: InvoiceItem[],
  serviceStart: string,
  serviceEnd: string,
  pricingMultiplier: number,
  idPrefix: string,
): InvoiceItem[] => sourceItems.map((item,index)=>recalculateInvoiceItem({
  ...applyRecurringPricingMultiplier(item,pricingMultiplier),
  id: `${idPrefix}-${index}`,
  servicePeriodStart: serviceStart || item.servicePeriodStart,
  servicePeriodEnd: serviceEnd || item.servicePeriodEnd,
}));

const calculatePricedItems = (
  template: RecurringInvoiceTemplate,
  serviceStart: string,
  serviceEnd: string,
  idPrefix = 'rec',
): RecurringPricedItemsResult => {
  const scheduledPriceChange = getApplicableRecurringPriceChange(template,serviceStart);
  const effectiveTemplate: RecurringInvoiceTemplate = scheduledPriceChange?.targetTotalInclTax
    ? {...template,pricingMode:'custom_total',pricingMultiplier:1,pricingTargetTotalInclTax:scheduledPriceChange.targetTotalInclTax}
    : template;
  const targetTotal = getRecurringPricingTargetTotal(effectiveTemplate);
  const configuredMultiplier = getRecurringPricingMultiplier(effectiveTemplate);
  const baseItems = recalculatePricedItems(template.items || [],serviceStart,serviceEnd,1,idPrefix);
  const baseFinancials = calculateInvoiceEditorFinancials({items: baseItems});

  if (!targetTotal) {
    const items = configuredMultiplier===1
      ? baseItems
      : recalculatePricedItems(template.items || [],serviceStart,serviceEnd,configuredMultiplier,idPrefix);
    const financials = calculateInvoiceEditorFinancials({items});
    return {...financials,items,roundingAdjustment:0,effectiveMultiplier:configuredMultiplier};
  }

  if (!(baseFinancials.total > 0)) {
    throw new Error('Custom annual total requires a template invoice with a positive total.');
  }

  // Binary-search the largest normal InvoiceFlow price scale that does not
  // exceed the requested GST-inclusive total. This keeps the existing tax
  // engine authoritative and avoids changing GST treatment on template lines.
  let low=0;
  let high=Math.max(targetTotal/baseFinancials.total,0.000001);
  let highItems=recalculatePricedItems(template.items || [],serviceStart,serviceEnd,high,idPrefix);
  let highTotal=calculateInvoiceEditorFinancials({items:highItems}).total;
  while(highTotal<targetTotal && high<1000000) {
    low=high;
    high*=2;
    highItems=recalculatePricedItems(template.items || [],serviceStart,serviceEnd,high,idPrefix);
    highTotal=calculateInvoiceEditorFinancials({items:highItems}).total;
  }
  for(let step=0;step<56;step+=1) {
    const mid=(low+high)/2;
    const candidate=recalculatePricedItems(template.items || [],serviceStart,serviceEnd,mid,idPrefix);
    const total=calculateInvoiceEditorFinancials({items:candidate}).total;
    if(total<=targetTotal) low=mid;
    else high=mid;
  }

  let items=recalculatePricedItems(template.items || [],serviceStart,serviceEnd,low,idPrefix);
  let financials=calculateInvoiceEditorFinancials({items});
  const roundingAdjustment=roundMoney(targetTotal-financials.total);

  // Some GST-inclusive totals cannot be represented to the cent by one
  // taxable line after standard line/GST rounding (e.g. $6,000.00 at 10%).
  // Add only the positive cent-level difference as a GST-free rounding line,
  // keeping the requested final total exact and the source template unchanged.
  if(roundingAdjustment>0) {
    const adjustment=recalculateInvoiceItem({
      id:`${idPrefix}-rounding`,
      description:'Annual pricing rounding adjustment',
      quantity:1,
      unitPrice:roundingAdjustment,
      discount:0,
      taxTreatment:'no_gst',
      taxRate:0,
      subtotal:roundingAdjustment,
      taxAmount:0,
      total:roundingAdjustment,
      servicePeriodStart:serviceStart || undefined,
      servicePeriodEnd:serviceEnd || undefined,
    });
    items=[...items,adjustment];
    financials=calculateInvoiceEditorFinancials({items});
  }

  if(roundMoney(financials.total)!==targetTotal) {
    throw new Error(`Unable to reconcile custom annual total exactly. Requested ${targetTotal.toFixed(2)}, calculated ${financials.total.toFixed(2)}.`);
  }

  return {...financials,items,roundingAdjustment,effectiveMultiplier:low};
};

export const calculateRecurringPricingPreview = (
  template: RecurringInvoiceTemplate,
  servicePeriodStart = '',
): Omit<RecurringPricedItemsResult,'items'> => {
  const result=calculatePricedItems(template,servicePeriodStart,'', 'preview');
  const {items: _items,...preview}=result;
  return preview;
};

const isProfileDue = (profile: RecurringProfile | any, today = getLocalToday()): boolean => {
  if (profile.status !== 'active' || profile.autoGenerateDraft !== true) return false;
  const next = String(profile.nextInvoiceDate || '').slice(0, 10);
  if (!next || next > today) return false;
  const serviceStart = String(profile.servicePeriodStart || profile.startDate || next).slice(0, 10);
  const endDate = String(profile.endDate || '').slice(0, 10);
  if (endDate && serviceStart > endDate) return false;
  return true;
};

const getTemplate = (profile: RecurringProfile | any): RecurringInvoiceTemplate => profile.baseInvoiceTemplate as RecurringInvoiceTemplate;

const validateRecurringProfileForGeneration = (profile: RecurringProfile | any): void => {
  if (!profile?.id) throw new Error('Save the recurring profile before generating an invoice.');
  if (!profile.clientId) throw new Error('Recurring profile requires a client.');
  if (!profile.nextInvoiceDate) throw new Error('Recurring profile requires a next invoice date.');
  const template = getTemplate(profile);
  if (!template || !Array.isArray(template.items) || template.items.length === 0) {
    throw new Error('Select a template invoice with at least one line item before generating recurring invoices.');
  }
};

const calculateDueDate = (issueDate: string, rule: RecurringDueDateRule): string => {
  if (rule === 'immediate') return issueDate;
  if (rule === 'net_7') return addDays(issueDate,7);
  return addDays(issueDate, rule === 'net_30' ? 30 : 14);
};

const shouldRetryInvoiceNumber = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error || '');
  return /invoiceNumber|invoice number|unique|already exists|constraint/i.test(message);
};

interface RecurringInvoiceCreationResult {
  invoice: Invoice;
  existingCycle: boolean;
}

const findInvoiceForCycleMarker = (invoices: Invoice[], marker: string): Invoice | undefined =>
  invoices.find(invoice => String(invoice.internalNotes || '').includes(marker));

const createInvoiceWithNumberRetry = async (
  invoiceBase: Omit<Invoice, 'id' | 'invoiceNumber'>,
  cycleMarker: string,
): Promise<RecurringInvoiceCreationResult> => {
  let lastError: unknown;
  for (let attempt = 0; attempt < 3; attempt += 1) {
    const latestInvoices = await Repository.invoices.getAll();
    const concurrentCycleInvoice = findInvoiceForCycleMarker(latestInvoices, cycleMarker);
    if (concurrentCycleInvoice) return { invoice: concurrentCycleInvoice, existingCycle: true };

    const defs = Repository.settings.getInvoiceDefaults();
    const invoiceNumber = getNextInvoiceNumber(latestInvoices, defs.prefix || 'INV', defs.nextNumber || 1001);
    try {
      const invoice = await appendRecord(Repository.invoices, { ...invoiceBase, invoiceNumber }) as Invoice;
      return { invoice, existingCycle: false };
    } catch (error) {
      lastError = error;
      if (!shouldRetryInvoiceNumber(error)) throw error;

      const invoicesAfterCollision = await Repository.invoices.getAll();
      const cycleInvoiceAfterCollision = findInvoiceForCycleMarker(invoicesAfterCollision, cycleMarker);
      if (cycleInvoiceAfterCollision) return { invoice: cycleInvoiceAfterCollision, existingCycle: true };
      if (attempt === 2) throw error;
    }
  }
  throw lastError instanceof Error ? lastError : new Error('Unable to allocate a unique invoice number.');
};

const updateInvoiceCounterBestEffort = async (invoice: Invoice): Promise<void> => {
  try {
    const defs = Repository.settings.getInvoiceDefaults();
    const match = String(invoice.invoiceNumber || '').match(/(\d+)$/);
    if (!match) return;
    const issuedNumber = Number(match[1]);
    await saveSettings({ invoice: { ...defs, nextNumber: Math.max(Number(defs.nextNumber || 1001), issuedNumber + 1) } });
  } catch (error) {
    console.warn('Recurring invoice was created, but the cached invoice counter could not be updated:', error);
  }
};

const dedupeEmails = (emails: string[]): string[] => {
  const unique = Array.from(new Set(emails.map(email => String(email || '').trim().toLowerCase()).filter(email => email && isValidEmail(email))));
  return unique;
};

const splitRecipients = (value?: string): string[] => String(value || '').split(',').map(value => value.trim()).filter(Boolean);

const queueRecurringInvoiceEmail = async (invoice: Invoice, profile: RecurringProfile | any): Promise<boolean> => {
  const settings = await loadSettings();
  if (!verifyLiveBusinessSettings()) throw new Error('Business settings could not be verified for automatic invoice sending.');
  const [clients, contacts, templates, jobs, attachments] = await Promise.all([
    Repository.clients.getAll(),
    Repository.contacts.getAll(),
    Repository.emailTemplates.getAll(),
    Repository.jobs.getAll(),
    Repository.attachments.getAll(),
  ]);
  const client = (clients as Client[]).find(item => item.id === invoice.clientId);
  if (!client) throw new Error('Recurring invoice client could not be found for automatic sending.');
  const automation = settings.automation;
  const profileRecipients = profile.defaultRecipients || { to: [], cc: [], bcc: [] };
  const useProfileRecipients = Array.isArray(profileRecipients.to) && profileRecipients.to.length > 0;
  const to = dedupeEmails(useProfileRecipients
    ? profileRecipients.to
    : [client.email || '', ...String(client.additionalEmails || '').split(',')]);
  const cc = dedupeEmails([
    ...(Array.isArray(profileRecipients.cc) ? profileRecipients.cc : []),
    ...(!useProfileRecipients && automation.recipientBehavior?.includeContacts !== false
      ? (contacts as any[]).filter(contact => contact.clientId === invoice.clientId && contact.includeOnInvoices === true).map(contact => contact.email || '')
      : []),
    ...splitRecipients(automation.defaultCc),
  ]);
  const bcc = dedupeEmails([
    ...(Array.isArray(profileRecipients.bcc) ? profileRecipients.bcc : []),
    ...splitRecipients(automation.defaultBcc),
  ]);
  const validation = validateAndDedupeRecipients(
    to,
    [...cc, ...splitRecipients(automation.permanentAuditCc)],
    [...bcc, ...splitRecipients(automation.permanentAuditBcc)],
  );
  if (!validation.isValid) throw new Error(validation.error || 'Recurring invoice has no valid email recipient.');

  const activeTemplate = (templates as any[]).find(template => template.templateType === 'invoice_sent' && template.isActive);
  const fallback = getProfessionalInvoiceEmailDraft();
  const selectedTemplate = {
    subject: automation.invoiceEmailSubjectTemplate?.trim() || activeTemplate?.subject?.trim() || fallback.subject,
    body: automation.invoiceEmailBodyTemplate?.trim() || activeTemplate?.body?.trim() || fallback.body,
  };
  const renderClient = { ...client, name: client.name?.trim() || client.companyName?.trim() || 'Customer' };
  const job = (jobs as any[]).find(item => item.id === invoice.jobId);
  const rendered = renderEmailTemplate(selectedTemplate as any, invoice, renderClient, settings.business, [], job?.jobName);
  const pdf = await generateInvoicePdf(invoice, client, job, [], settings.business, settings.invoice);
  const file = new File([pdf.pdfBlob], pdf.filename, { type: 'application/pdf' });
  const snapshot = await Repository.pdfSnapshots.create({
    invoiceId: invoice.id,
    invoiceNumber: invoice.invoiceNumber,
    exportedAt: new Date().toISOString(),
    invoiceTotal: invoice.total,
    balanceDue: invoice.balanceDue,
    pdfFilename: pdf.filename,
    fileReference: pdf.filename,
    file: isPocketBaseMode() ? file : undefined,
    termsSnapshot: invoice.termsSnapshot,
    notesSnapshot: invoice.notes,
    paymentSummarySnapshot: `Paid: ${invoice.amountPaid || 0};Balance: ${invoice.balanceDue || 0}`,
  });

  const requestedAttachmentIds = Array.isArray(profile.defaultAttachmentIds) ? profile.defaultAttachmentIds : [];
  const selectedAttachments = (attachments as any[]).filter(attachment =>
    requestedAttachmentIds.includes(attachment.id)
    && attachment.visibility === 'client'
    && attachment.archived !== true
    && attachment.file,
  );
  const emailJob = await Repository.emailJobs.create({
    invoiceId: invoice.id,
    clientId: invoice.clientId,
    jobId: invoice.jobId || '',
    toRecipients: validation.to,
    ccRecipients: validation.cc,
    bccRecipients: validation.bcc,
    subject: rendered.subject,
    body: rendered.body,
    templateType: 'invoice_sent',
    pdfSnapshotId: snapshot.id,
    selectedAttachmentIds: selectedAttachments.map(item => item.id),
    attachmentNamesSnapshot: [pdf.filename, ...selectedAttachments.map(item => item.fileName)],
    attachmentCount: 1 + selectedAttachments.length,
    hasInternalOnlyAttachmentWarning: false,
    status: 'queued',
    priority: 1,
    retryCount: 0,
    createdAt: new Date().toISOString(),
  });
  await updateRecord(Repository.invoices, invoice.id, {
    ...invoice,
    status: 'send_queued',
    pdfSnapshotIds: [...(invoice.pdfSnapshotIds || []), snapshot.id],
    updatedAt: new Date().toISOString(),
  });
  await Repository.timeline.create({
    entityType: 'invoice',
    entityId: invoice.id,
    eventType: 'email_queued',
    description: `Recurring invoice ${invoice.invoiceNumber} queued for automatic sending`,
    createdAt: new Date().toISOString(),
    createdBy: 'automation',
  });
  try {
    await dispatchEmailJob(emailJob.id);
  } catch (error) {
    console.warn('Recurring invoice email remains queued after immediate relay dispatch failed:', error);
  }
  return true;
};

const advanceProfileAfterGeneration = async (profile: RecurringProfile | any, invoice: Invoice, cycleKey: string): Promise<RecurringProfile> => {
  const schedule = getRecurringSchedule(profile);
  const currentServiceStart = String(profile.servicePeriodStart || profile.startDate || profile.nextInvoiceDate).slice(0, 10);
  const calculatedCurrentPeriod = calculateRecurringServicePeriod(currentServiceStart, schedule);
  const nextServiceStart = advanceRecurringDate(calculatedCurrentPeriod.start, schedule);
  const nextServicePeriod = calculateRecurringServicePeriod(nextServiceStart, schedule);
  const billingInAdvanceDays = Math.max(0, Number(profile.billingInAdvanceDays || 0));
  const nextInvoiceDate = calculateRecurringInvoiceDate(nextServicePeriod.start, billingInAdvanceDays);
  const updated = await updateRecord(Repository.recurringProfiles, profile.id, {
    ...profile,
    frequency: getStoredRecurringFrequency(schedule),
    baseInvoiceTemplate: { ...(profile.baseInvoiceTemplate || {}), schedule },
    servicePeriodStart: nextServicePeriod.start,
    servicePeriodEnd: nextServicePeriod.end,
    nextInvoiceDate,
    lastGeneratedInvoiceId: invoice.id,
    lastGeneratedCycleKey: cycleKey,
    failureCount: 0,
    updatedAt: new Date().toISOString(),
  });
  return updated as RecurringProfile;
};

const recordAutomationRun = async (status: 'success' | 'failed' | 'partial', profile: RecurringProfile | any, cycleKey: string, message: string): Promise<void> => {
  try {
    await Repository.automationRuns.create({
      provider: 'other',
      runType: 'recurring',
      status,
      processedCount: status === 'failed' ? 0 : 1,
      errorMessage: `Profile ${profile.id} (${profile.name}) cycle ${cycleKey}: ${message}`,
      startedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
    });
  } catch (error) {
    console.warn('Non-blocking recurring automation audit write failed:', error);
  }
};

export const generateRecurringInvoice = async (
  profile: RecurringProfile | any,
  options: { force?: boolean; autoSend?: boolean } = {},
): Promise<RecurringGenerationResult> => {
  validateRecurringProfileForGeneration(profile);
  const currentServiceStart = String(profile.servicePeriodStart || profile.startDate || profile.nextInvoiceDate || '').slice(0, 10);
  const configuredEndDate = String(profile.endDate || '').slice(0, 10);
  if (configuredEndDate && currentServiceStart > configuredEndDate) {
    return { status: 'skipped', message: 'Recurring profile has reached its configured end date.' };
  }
  if (!options.force && !isProfileDue(profile)) {
    return { status: 'skipped', message: 'Recurring profile is not due yet.' };
  }

  const cycleKey = cycleKeyForProfile(profile);
  const marker = markerForCycle(cycleKey);
  const existingInvoices = await Repository.invoices.getAll();
  const existing = existingInvoices.find(invoice => String(invoice.internalNotes || '').includes(marker));
  if (existing) {
    if (profile.lastGeneratedCycleKey !== cycleKey || profile.lastGeneratedInvoiceId !== existing.id) {
      await advanceProfileAfterGeneration(profile, existing, cycleKey);
    }
    await recordAutomationRun('success', profile, cycleKey, `Existing invoice ${existing.invoiceNumber} reconciled; no duplicate created.`);
    return { status: 'reconciled', invoice: existing, message: `Existing recurring invoice ${existing.invoiceNumber} was found; no duplicate was created.` };
  }

  const template = getTemplate(profile);
  const schedule = getRecurringSchedule(profile);
  const serviceStart = String(profile.servicePeriodStart || profile.startDate || profile.nextInvoiceDate).slice(0, 10);
  const calculatedPeriod = calculateRecurringServicePeriod(serviceStart, schedule);
  const serviceEnd = String(profile.servicePeriodEnd || calculatedPeriod.end).slice(0, 10);
  const pricedInvoice = calculatePricedItems(template, serviceStart, serviceEnd, `rec-${Date.now()}`);
  const items = pricedInvoice.items;
  const financials = pricedInvoice;
  const clients = await Repository.clients.getAll();
  const client = clients.find(item => item.id === profile.clientId) as Client | undefined;
  if (!client) throw new Error('Recurring invoice client could not be found.');
  const issueDate = String(profile.nextInvoiceDate).slice(0, 10);
  const now = new Date().toISOString();
  const invoiceBase: Omit<Invoice, 'id' | 'invoiceNumber'> = {
    clientId: profile.clientId,
    jobId: profile.jobId || '',
    issueDate,
    dueDate: calculateDueDate(issueDate, getRecurringDueDateRule(profile)),
    status: 'draft',
    currency: template.currency || client.currency || 'AUD',
    items,
    pdfSnapshotIds: [],
    subtotal: financials.subtotal,
    taxAmount: financials.taxAmount,
    total: financials.total,
    amountPaid: 0,
    balanceDue: financials.total,
    notes: template.notes || '',
    termsSnapshot: template.termsSnapshot || '',
    internalNotes: appendInternalMarker(template.internalNotes, marker),
    createdAt: now,
    updatedAt: now,
  };

  let invoice: Invoice;
  try {
    const creationResult = await createInvoiceWithNumberRetry(invoiceBase, marker);
    invoice = creationResult.invoice;
    if (creationResult.existingCycle) {
      if (profile.lastGeneratedCycleKey !== cycleKey || profile.lastGeneratedInvoiceId !== invoice.id) {
        await advanceProfileAfterGeneration(profile, invoice, cycleKey);
      }
      await recordAutomationRun('success', profile, cycleKey, `Concurrent generation resolved to existing invoice ${invoice.invoiceNumber}; no duplicate created.`);
      return {
        status: 'reconciled',
        invoice,
        message: `Recurring cycle already generated as ${invoice.invoiceNumber}; no duplicate invoice or email was created.`,
      };
    }
    await updateInvoiceCounterBestEffort(invoice);
    await Repository.timeline.create({
      entityType: 'invoice',
      entityId: invoice.id,
      eventType: 'recurring_generated',
      description: `Recurring profile ${profile.name} generated invoice ${invoice.invoiceNumber} for ${serviceStart} to ${serviceEnd}`,
      createdAt: new Date().toISOString(),
      createdBy: 'automation',
    });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown recurring invoice generation error';
    try {
      await updateRecord(Repository.recurringProfiles, profile.id, { ...profile, failureCount: Number(profile.failureCount || 0) + 1, updatedAt: new Date().toISOString() });
    } catch (updateError) {
      console.warn('Unable to increment recurring failure counter:', updateError);
    }
    await recordAutomationRun('failed', profile, cycleKey, message);
    throw error;
  }

  let advancedProfile: RecurringProfile;
  try {
    advancedProfile = await advanceProfileAfterGeneration(profile, invoice, cycleKey);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Profile cycle could not be advanced';
    await recordAutomationRun('partial', profile, cycleKey, `Invoice ${invoice.invoiceNumber} was created, but ${message}`);
    throw new Error(`Invoice ${invoice.invoiceNumber} was created safely, but the recurring profile could not advance: ${message}. The cycle marker on the invoice prevents normal re-generation after refresh.`);
  }

  let emailQueued = false;
  const shouldAutoSend = options.autoSend ?? advancedProfile.autoSend === true;
  if (shouldAutoSend) {
    try {
      emailQueued = await queueRecurringInvoiceEmail(invoice, advancedProfile);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Automatic email queueing failed';
      try {
        await updateRecord(Repository.recurringProfiles, advancedProfile.id, { ...advancedProfile, failureCount: Number(advancedProfile.failureCount || 0) + 1, updatedAt: new Date().toISOString() });
      } catch (updateError) {
        console.warn('Unable to increment recurring send failure counter:', updateError);
      }
      await recordAutomationRun('partial', profile, cycleKey, `Invoice ${invoice.invoiceNumber} created; automatic sending failed: ${message}`);
      return {
        status: 'generated',
        invoice,
        emailQueued: false,
        message: `Invoice ${invoice.invoiceNumber} was generated, but automatic sending could not be queued: ${message}`,
      };
    }
  }

  await recordAutomationRun('success', profile, cycleKey, `Invoice ${invoice.invoiceNumber} generated${emailQueued ? ' and queued for sending' : ''}.`);
  return {
    status: 'generated',
    invoice,
    emailQueued,
    message: `Invoice ${invoice.invoiceNumber} generated${emailQueued ? ' and queued for sending' : ''}.`,
  };
};

export interface RecurringProcessingSummary {
  checked: number;
  generated: number;
  reconciled: number;
  failed: number;
  messages: string[];
}

export const processDueRecurringProfiles = async (options: { force?: boolean } = {}): Promise<RecurringProcessingSummary> => {
  const now = Date.now();
  if (!options.force && activeProcessor) return activeProcessor;
  if (!options.force && now - lastProcessorStartedAt < PROCESSOR_INTERVAL_LOCK_MS) {
    return { checked: 0, generated: 0, reconciled: 0, failed: 0, messages: [] };
  }
  lastProcessorStartedAt = now;
  const run = async (): Promise<RecurringProcessingSummary> => {
    const profiles = await Repository.recurringProfiles.getAll();
    const dueProfiles = profiles.filter(profile => isProfileDue(profile));
    const summary: RecurringProcessingSummary = { checked: dueProfiles.length, generated: 0, reconciled: 0, failed: 0, messages: [] };
    for (const profile of dueProfiles) {
      try {
        const result = await generateRecurringInvoice(profile);
        if (result.status === 'generated') summary.generated += 1;
        if (result.status === 'reconciled') summary.reconciled += 1;
        if (result.status !== 'skipped') summary.messages.push(result.message);
      } catch (error) {
        summary.failed += 1;
        summary.messages.push(`${profile.name}: ${error instanceof Error ? error.message : 'Recurring generation failed'}`);
      }
    }
    if (summary.generated > 0 || summary.reconciled > 0) {
      window.dispatchEvent(new CustomEvent('invoiceflow:refresh-data'));
    }
    return summary;
  };
  activeProcessor = run();
  try {
    return await activeProcessor;
  } finally {
    activeProcessor = null;
  }
};